import { Component } from '@theme/component';

/**
 * @typedef {Object} AxSlideshowRefs
 * @property {HTMLElement} swiper - The <swiper-container> element.
 * @property {HTMLElement} fallback - The SSR fallback container with the initial slides.
 * @property {HTMLButtonElement} [prev] - Previous-slide control button.
 * @property {HTMLButtonElement} [next] - Next-slide control button.
 * @property {HTMLButtonElement} [playPause] - Play/pause autoplay control button.
 */

/**
 * @extends {Component<AxSlideshowRefs>}
 */
class AxSlideshowComponent extends Component {
  requiredRefs = ['swiper'];

  #swiper = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  #abortController = new AbortController();

  async connectedCallback() {
    super.connectedCallback();

    await customElements.whenDefined('swiper-container');
    if (this.#abortController.signal.aborted) return;

    const { swiper, fallback } = this.refs;
    if (!fallback) return;
    const slideElements = [...fallback.children];
    if (!slideElements.length) return;

    const reservedHeight = this.getBoundingClientRect().height;
    if (reservedHeight > 0) {
      this.style.minHeight = `${reservedHeight}px`;
    }

    for (const slideEl of slideElements) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slideEl);
      swiper.appendChild(swiperSlide);
    }
    this.#slides = [...swiper.querySelectorAll('swiper-slide')];
    fallback.remove();

    const autoplayDelay = parseInt(this.dataset.autoplay ?? '', 10);
    const autoplay = Number.isFinite(autoplayDelay) && autoplayDelay > 0
      ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
      : false;

    Object.assign(swiper, {
      slidesPerView: 1,
      loop: this.#slides.length > 1,
      autoplay,
      grabCursor: this.#slides.length > 1,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      pagination: this.#slides.length > 1 ? { clickable: true } : false,
    });

    swiper.initialize();
    swiper.removeAttribute('init');

    requestAnimationFrame(() => {
      this.style.minHeight = '';
    });

    this.#swiper = swiper.swiper;
    if (!this.#swiper) return;

    this.#preloadAdjacent(this.#swiper.realIndex ?? 0, { currentOnly: true });

    const onSlideChange = () => {
      this.#preloadAdjacent(this.#swiper?.realIndex ?? 0);
    };

    this.#swiper.on('slideChangeTransitionStart', onSlideChange);
    this.#swiper.on('touchStart', onSlideChange);

    if (this.refs.playPause && this.#swiper.autoplay) {
      this.#swiper.on('autoplayStart', () => this.#syncPlayPauseState('playing'));
      this.#swiper.on('autoplayStop', () => this.#syncPlayPauseState('paused'));
    }

    this.#swiper.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    try {
      this.#swiper?.destroy(true, true);
    } catch (_) {
      // Swiper can throw during teardown if already destroyed
    }
    this.#swiper = null;
  }

  handlePrev() {
    this.#swiper?.slidePrev();
  }

  handleNext() {
    this.#swiper?.slideNext();
  }

  handlePlayPause() {
    const { playPause } = this.refs;
    if (!playPause || !this.#swiper?.autoplay) return;

    if (playPause.dataset.state === 'playing') {
      this.#swiper.autoplay.stop();
    } else {
      this.#swiper.autoplay.start();
    }
  }

  /**
   * @param {'playing' | 'paused'} state
   */
  #syncPlayPauseState(state) {
    const { playPause } = this.refs;
    if (!playPause) return;
    playPause.dataset.state = state;
    const label = state === 'playing' ? playPause.dataset.labelPause : playPause.dataset.labelPlay;
    if (label) playPause.setAttribute('aria-label', label);
  }

  #preloadAdjacent(index, { currentOnly = false } = {}) {
    const count = this.#slides.length;
    if (!count) return;
    const offsets = currentOnly ? [0] : [0, 1];
    for (const offset of offsets) {
      const target = this.#slides[(index + offset + count) % count];
      if (target?.querySelector('[data-lazy-keep]')) {
        document.dispatchEvent(
          new CustomEvent('removeLazyKeep', {
            detail: { lazyParentElement: target },
          })
        );
      }
    }
  }
}

if (!customElements.get('ax-slideshow')) {
  customElements.define('ax-slideshow', AxSlideshowComponent);
}
